/**
 * 飞书错误上报 - 错误队列管理
 */

import type { ErrorInfo, FeishuErrorReporterConfig } from './types';
import { reportErrorToFeishu } from './reporter';

// 错误上报队列
let errorQueue: ErrorInfo[] = [];
let isReporting = false;

// 保存配置引用
let currentConfig: FeishuErrorReporterConfig | null = null;

/**
 * 设置配置引用
 */
export function setQueueConfig(config: FeishuErrorReporterConfig | null): void {
  currentConfig = config;
}

/**
 * 检查是否为弱网环境
 */
function isWeakNetwork(): boolean {
  // 检测是否离线
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return true;
  }

  // 使用 Network Information API 检测网络质量
  const connection =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
  if (connection) {
    const effectiveType = connection.effectiveType;
    // slow-2g, 2g 视为弱网
    if (effectiveType === 'slow-2g' || effectiveType === '2g') {
      return true;
    }
  }

  return false;
}

/**
 * 完全忽略的错误关键词（无论网络状态）
 * 这些错误通常是用户行为导致的，没有调试价值
 */
const ALWAYS_IGNORED_KEYWORDS = [
  'Request aborted', // 用户离开页面导致的请求中止
  'aborted', // 请求被中止
];

/**
 * 网络相关错误的关键词
 * 弱网环境下这些错误没有调试价值
 */
const NETWORK_ERROR_KEYWORDS = [
  'Network Error',
  'Failed to fetch',
  'NetworkError',
  'Load failed',
  '资源加载失败',
  'net::',
  'ECONNREFUSED',
];

/**
 * 检查是否为网络相关错误
 */
function isNetworkRelatedError(errorInfo: Partial<ErrorInfo>): boolean {
  // 资源加载错误
  if (errorInfo.errorType === 'resource-error') {
    return true;
  }

  // 检查错误消息是否包含网络相关关键词
  const message = errorInfo.message || '';
  return NETWORK_ERROR_KEYWORDS.some((keyword) => message.includes(keyword));
}

/**
 * 检查是否通过采样
 */
function passSampling(): boolean {
  const sampleRate = currentConfig?.sampleRate ?? 1.0;
  return Math.random() < sampleRate;
}

/**
 * 批量处理错误队列
 */
async function processErrorQueue(): Promise<void> {
  if (isReporting || errorQueue.length === 0 || !currentConfig) {
    return;
  }

  isReporting = true;

  try {
    // 每次最多处理5个错误
    const errorsToProcess = errorQueue.splice(0, 5);

    for (const error of errorsToProcess) {
      await reportErrorToFeishu(error, currentConfig);
      // 避免请求过于频繁
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  } catch (error) {
    console.error('批量处理错误队列失败:', error);
  } finally {
    isReporting = false;

    // 如果还有错误待处理，延迟处理
    if (errorQueue.length > 0) {
      setTimeout(processErrorQueue, 1000);
    }
  }
}

/**
 * 添加错误到队列
 */
export function addErrorToQueue(errorInfo: Partial<ErrorInfo>, config: FeishuErrorReporterConfig): void {
  const message = errorInfo.message || '';

  // 完全忽略的错误（无论网络状态）
  if (ALWAYS_IGNORED_KEYWORDS.some((keyword) => message.includes(keyword))) {
    return;
  }

  // 弱网环境下，只过滤网络相关错误，其他错误仍然上报
  if (isWeakNetwork() && isNetworkRelatedError(errorInfo)) {
    return;
  }

  // 采样检查
  if (!passSampling()) {
    return;
  }

  // 获取用户信息
  const userInfo = config.getUserInfo?.() || {};

  // 获取当前页面路径
  const url = typeof window !== 'undefined' ? window.location.href : 'unknown';

  // 获取用户代理
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown';

  const error: ErrorInfo = {
    message: errorInfo.message || '未知错误',
    stack: errorInfo.stack,
    filename: errorInfo.filename,
    lineno: errorInfo.lineno,
    colno: errorInfo.colno,
    timestamp: Date.now(),
    userAgent,
    url,
    unionId: userInfo.unionId,
    userId: userInfo.userId,
    username: userInfo.username,
    environment: config.environment,
    errorType: errorInfo.errorType || 'custom-error',
  };

  // 避免重复上报相同错误
  const isDuplicate = errorQueue.some(
    (existingError) =>
      existingError.message === error.message &&
      existingError.url === error.url &&
      Date.now() - existingError.timestamp < 5000, // 5秒内的相同错误视为重复
  );

  if (!isDuplicate) {
    errorQueue.push(error);
    // 立即处理队列
    processErrorQueue();
  }
}

/**
 * 获取错误队列状态（用于调试）
 */
export function getErrorQueueStatus(): { queueLength: number; isReporting: boolean } {
  return {
    queueLength: errorQueue.length,
    isReporting,
  };
}

/**
 * 清理错误队列
 */
export function clearErrorQueue(): void {
  errorQueue = [];
  isReporting = false;
}

/**
 * 重新添加错误到队列（上报失败时使用）
 */
export function reAddErrorToQueue(error: ErrorInfo): void {
  if (errorQueue.length < 10) {
    errorQueue.push(error);
  }
}
